import {
  Box, Table, Thead, Tbody, Tr, Th, Td, IconButton, Input, Text, Badge,
  HStack, Select, Flex, Spacer, useToast,
} from '@chakra-ui/react';
import FloatingAddButton from '../../components/FloatingAddButton';
import TableSkeleton from '../../components/TableSkeleton';
import { useEffect, useMemo, useState } from 'react';
import { MdEdit, MdVisibility, MdHistory, MdDescription, MdGavel } from 'react-icons/md';
import juzgadoApi from '../../services/juzgadoApi';
import type { AudienciaJuzgado } from './juzgadoTypes';
import ModalAudiencia from './ModalAudiencia';
import ModalCitatorio from './ModalCitatorio';
import ModalHistoricoJuzgado from './ModalHistoricoJuzgado';
import ModalDesarrolloAudiencia from './ModalDesarrolloAudiencia';

export default function TablaAudiencias() {
  const [audiencias, setAudiencias] = useState<AudienciaJuzgado[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('');
  const [filtroEstatus, setFiltroEstatus] = useState('');
  const toast = useToast();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'new' | 'edit' | 'view'>('new');
  const [audienciaSel, setAudienciaSel] = useState<AudienciaJuzgado | null>(null);

  const [modalCitOpen, setModalCitOpen] = useState(false);
  const [modalHistOpen, setModalHistOpen] = useState(false);
  const [modalDevOpen, setModalDevOpen] = useState(false);

  const norm = (v?: string | null) => (v || '').toLowerCase();

  const fmtFecha = (iso?: string) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? iso : d.toLocaleDateString('es-MX');
  };

  const colorEstatus = (e?: string) => {
    const s = (e || '').toLowerCase();
    if (s === 'programada') return 'blue';
    if (s === 'celebrada') return 'green';
    if (s.includes('cancelada')) return 'gray';
    if (s === 'reprogramada') return 'orange';
    if (s === 'no compareció') return 'red';
    return 'gray';
  };

  const fetchAudiencias = async () => {
    try {
      setLoading(true);
      const data = await juzgadoApi.listarAudiencias(filtroEstatus ? { estatus: filtroEstatus } : undefined);
      setAudiencias(data);
    } catch (e: any) {
      toast({ title: 'Error cargando audiencias', description: e.message, status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAudiencias(); }, [filtroEstatus]);

  const filtradas = useMemo(() => {
    const f = norm(filtro);
    if (!f) return audiencias;
    return audiencias.filter(a =>
      norm(a.folioAudiencia).includes(f) ||
      norm(a.expedienteRef).includes(f) ||
      norm(a.imputado).includes(f) ||
      norm(a.juezAsignado).includes(f) ||
      norm(a.tipoAudiencia).includes(f)
    );
  }, [audiencias, filtro]);

  const handleGuardado = (a: AudienciaJuzgado) => {
    setAudiencias(prev => {
      const idx = prev.findIndex(x => x.id === a.id);
      if (idx >= 0) { const c = [...prev]; c[idx] = a; return c; }
      return [a, ...prev];
    });
    setModalOpen(false);
  };

  return (
    <Box p={4} pt="79px" position="relative">
      <Text fontSize="2xl" fontWeight="bold" mb={4} color="#5b182b">Audiencias</Text>

      <Flex mb={4} gap={2} wrap="wrap">
        <Input
          placeholder="Buscar por folio, expediente, imputado..."
          value={filtro}
          onChange={e => setFiltro(e.target.value)}
          maxW="340px"
          borderColor="#bb8c52"
          _focus={{ borderColor: '#5b182b', boxShadow: '0 0 0 1px #5b182b' }}
        />
        <Select
          placeholder="Todos los estatus"
          value={filtroEstatus}
          onChange={e => setFiltroEstatus(e.target.value)}
          maxW="200px"
          borderColor="#bb8c52"
        >
          {['Programada','Reprogramada','Cancelada','En espera de citación','Celebrada','No compareció','Cerrada'].map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </Select>
        <Spacer />
      </Flex>

      {loading ? (
        <Box bg="white" borderRadius="lg" shadow="sm" overflow="hidden">
          <TableSkeleton rows={8} columns={7} />
        </Box>
      ) : (
        <Box bg="white" borderRadius="lg" shadow="sm" overflowX="auto">
          <Table variant="simple" size="sm">
            <Thead bg="#5b182b">
              <Tr>
                <Th color="white">Folio</Th>
                <Th color="white">Tipo</Th>
                <Th color="white">Imputado</Th>
                <Th color="white">Fecha</Th>
                <Th color="white">Juez</Th>
                <Th color="white">Estatus</Th>
                <Th color="white">Acciones</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filtradas.length === 0 && (
                <Tr><Td colSpan={7} textAlign="center" color="gray.400" py={8}>Sin audiencias</Td></Tr>
              )}
              {filtradas.map(a => (
                <Tr key={a.id} _hover={{ bg: 'gray.50' }}>
                  <Td fontWeight="medium" color="#5b182b">{a.folioAudiencia}</Td>
                  <Td fontSize="sm">{a.tipoAudiencia}</Td>
                  <Td>{a.imputado ?? '—'}</Td>
                  <Td whiteSpace="nowrap" fontSize="sm">{fmtFecha(a.fechaProgramada)} {a.horaInicio}</Td>
                  <Td fontSize="sm">{a.juezAsignado ?? '—'}</Td>
                  <Td>
                    <Badge colorScheme={colorEstatus(a.estatus)} px={2} borderRadius="full">
                      {a.estatus}
                    </Badge>
                  </Td>
                  <Td>
                    <HStack spacing={1}>
                      <IconButton aria-label="Ver" icon={<MdVisibility />} size="sm" bg="#5b182b" color="white" _hover={{ bg: '#691f30' }}
                        onClick={() => { setAudienciaSel(a); setModalMode('view'); setModalOpen(true); }} />
                      <IconButton aria-label="Editar" icon={<MdEdit />} size="sm" bg="#bb8c52" color="white" _hover={{ bg: '#a67c47' }}
                        onClick={() => { setAudienciaSel(a); setModalMode('edit'); setModalOpen(true); }} />
                      <IconButton aria-label="Citatorio" icon={<MdDescription />} size="sm" colorScheme="teal" variant="solid"
                        onClick={() => { setAudienciaSel(a); setModalCitOpen(true); }} />
                      <IconButton aria-label="Desarrollo de audiencia" icon={<MdGavel />} size="sm" colorScheme="purple" variant="solid"
                        onClick={() => { setAudienciaSel(a); setModalDevOpen(true); }} title="Desarrollo en tiempo real / Acta" />
                      <IconButton aria-label="Historial" icon={<MdHistory />} size="sm" colorScheme="gray" variant="outline"
                        onClick={() => { setAudienciaSel(a); setModalHistOpen(true); }} />
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      <ModalAudiencia
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        mode={modalMode}
        audiencia={audienciaSel}
        onGuardado={handleGuardado}
      />

      <ModalCitatorio
        isOpen={modalCitOpen}
        onClose={() => setModalCitOpen(false)}
        audiencia={audienciaSel}
      />

      <ModalHistoricoJuzgado
        isOpen={modalHistOpen}
        onClose={() => setModalHistOpen(false)}
        entidad="AudienciaJuzgado"
        entidadId={audienciaSel?.id ?? 0}
        titulo={`Historial: ${audienciaSel?.folioAudiencia ?? ''}`}
      />

      <ModalDesarrolloAudiencia
        isOpen={modalDevOpen}
        onClose={() => setModalDevOpen(false)}
        audiencia={audienciaSel}
      />

      <FloatingAddButton text="Nueva Audiencia" onClick={() => {
        setAudienciaSel(null);
        setModalMode('new');
        setModalOpen(true);
      }} />
    </Box>
  );
}
